import { Stack } from '@mui/material';

import {
  getIntegratedAssetsFromValuation,
  getManuallyTrackedAssetsFromValuation,
} from '@/modules/assets/FullScreenStructuredAssetsModal/FullScreenStructuredAssetsModal.utils';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { EstateAccordionContentEmptyState } from '@/modules/estate/EstateDirectHoldings/components/EstateAccordionContentEmptyState';
import { HoldingsGroupAssets } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupAssets/HoldingsGroupAssets';
import { HoldingsGroupLiabilities } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupLiabilities/HoldingsGroupLiabilities';
import { HoldingsGroupPolicies } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupPolicies/HoldingsGroupPolicies';
import { extractPoliciesFromEntity } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupPolicies/HoldingsGroupPolicies.utils';
import { holdingsGroupHasAssets } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { getNodes } from '@/utils/graphqlUtils';

import { HoldingsGroupBusinesses } from '../../EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupBusinesses/HoldingsGroupBusinesses';
import { extractOwnedBusinessesFromEntity } from '../../EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupBusinesses/HoldingsGroupBusinesses.utils';

export interface EntityAccordionContentProps {
  entity: EstatePage_EntityFragment;
}

/**
 * Consolidated component for rendering entity assets, liabilities, insurance policies, and businesses.
 * Handles the common logic shared between EstateBusinessEntityCard, HoldingsGroupSection, and EstateTrustEntityCard.
 */
export function EntityAccordionContent({
  entity,
}: EntityAccordionContentProps) {
  // Get refetch function from context
  const { refetch: refetchLiabilities } = useEstateHoldingsContext();

  // Extract liabilities from entity connections
  const liabilities = [
    ...getNodes(entity.liabilitiesOwed),
    ...getNodes(entity.liabilitiesLent),
  ];

  // Extract valuation and assets data
  const designerAccount = entity?.subtype.designerAccount ?? null;
  const valuation = designerAccount?.valuations.edges?.[0]?.node ?? null;

  const integratedAssets = getIntegratedAssetsFromValuation(valuation);
  const manuallyTrackedAssets =
    getManuallyTrackedAssetsFromValuation(valuation);

  // Extract policies from entity
  const policies = extractPoliciesFromEntity(entity);
  const businesses = extractOwnedBusinessesFromEntity(entity);

  // Determine what to show
  const hasLiabilities = liabilities.length > 0;
  const hasAssets = holdingsGroupHasAssets(entity);
  const hasPolicies = policies.length > 0;
  const hasBusinesses = businesses.length > 0;

  // Show empty state if there are no assets, liabilities, policies, or businesses
  if (!hasLiabilities && !hasAssets && !hasPolicies && !hasBusinesses) {
    return <EstateAccordionContentEmptyState entityKind={entity.kind} />;
  }

  return (
    <Stack spacing={2}>
      <HoldingsGroupAssets
        holding={entity}
        valuation={valuation}
        integratedAssets={integratedAssets}
        manuallyTrackedAssets={manuallyTrackedAssets}
      />
      <HoldingsGroupBusinesses businesses={businesses} />
      <HoldingsGroupLiabilities
        liabilities={liabilities}
        refetch={refetchLiabilities || (() => {})}
      />
      <HoldingsGroupPolicies policies={policies} entity={entity} />
    </Stack>
  );
}
